import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Title from '../../../../components/Title';
import { AsqHeader, AsqFooter } from '../../../../components/Asq/AsqBranding';

export type AsqSectionResult = {
  id: number;
  title: string;
  total_score: number | null;
  cutoff_gray: number | null;
  cutoff_black: number | null;
};

export type AsqResultPayload = {
  title?: string;
  sections?: AsqSectionResult[];
  savedAt?: number;
};

// 共通: 0〜60 を 5点刻みの配列で提供
const useSteps = (max = 60) =>
  useMemo(() => {
    const arr: number[] = [];
    for (let v = 0; v <= max; v += 5) arr.push(v);
    return arr;
  }, [max]);

const roundTo5 = (v: number | null) => {
  if (v == null || Number.isNaN(v)) return null;
  return Math.round(v / 5) * 5; // 念のため丸め
};

// 丸ドット（中にスコアは表示しない）: ドットは常に中立デザイン。ゾーン色は帯背景で表現する
const Dot: React.FC<{
  step: number;
  total5: number | null;
}> = ({ step, total5 }) => {
  const isHit = total5 != null && step === total5;

  const base =
    'relative inline-flex items-center justify-center w-3 h-3 md:w-5 md:h-5 rounded-full border transition-colors shrink-0';
  const ring = isHit ? ' ring-2 ring-offset-1 ring-blue-500' : '';

  const colorCls = ' bg-white border-gray-300';

  return <span className={base + ' ' + colorCls + ring} aria-label={`${step}点`} />;
};

// ヘッダ行：5点刻み列（ボタン無しで数値のみ）
const StepsHeader: React.FC<{ steps: number[] }> = ({ steps }) => {
  return (
    <div className='flex items-center gap-1 md:gap-3 pl-2'>
      {/* 左側の固定列ラベル群 */}
      <div className='w-24 md:w-48 text-[8px] md:text-xs text-gray-600'>領域</div>
      <div className='w-12 md:w-20 text-[8px] md:text-xs text-gray-600 text-center'>黒カット</div>
      <div className='w-10 md:w-16 text-[8px] md:text-xs text-gray-600 text-center'>合計値</div>

      {/* 右側の5点刻みラベル：帯とセル寸法を行と揃える */}
      <div className='flex items-center gap-1 md:gap-2 overflow-x-auto py-0.5 px-1 md:px-2 rounded-md border border-gray-300'>
        {steps.map((s) => (
          <span
            key={s}
            className='w-4 h-4 md:w-9 md:h-9 inline-flex items-center justify-center text-[9px] text-gray-500 leading-none'
          >
            {s}
          </span>
        ))}
      </div>
    </div>
  );
};

// セクションごとの行
const SectionRow: React.FC<{
  sec: AsqSectionResult;
  steps: number[];
}> = ({ sec, steps }) => {
  const total5 = roundTo5(sec.total_score ?? null);
  const gray = sec.cutoff_gray ?? null;
  const black = sec.cutoff_black ?? null;

  return (
    <div className='flex items-center gap-1 md:gap-3 pl-2'>
      {/* 領域（セクション名） */}
      <div className='w-24 md:w-48 text-[10px] md:text-sm text-gray-800'>
        <span className='font-medium leading-tight whitespace-normal break-words' title={sec.title}>
          {sec.title}
        </span>
      </div>

      {/* カットオフ値（黒） */}
      <div className='w-14 md:w-20 text-[10px] md:text-sm text-gray-800 text-center tabular-nums'>{black ?? '-'}</div>

      {/* 合計値（5点刻み表示） */}
      <div className='w-10 md:w-16 text-[10px] md:text-sm text-gray-800 text-center tabular-nums'>{total5 ?? '-'}</div>

      {/* 5点刻み：帯全体の背景でゾーン色を表現し、その上に中立ドットを配置 */}
      <div
        className='flex items-center gap-1 md:gap-2 overflow-x-auto py-1 px-1 md:px-2 rounded-md border border-gray-300'
        style={(() => {
          const max = steps[steps.length - 1] ?? 60;
          const clamp = (v: number) => Math.max(0, Math.min(v, max));
          const b = typeof black === 'number' ? clamp(black) : null;
          const g = typeof gray === 'number' ? clamp(gray) : null;

          const toPct = (v: number) => (v / max) * 100;
          const bPct = b != null ? toPct(b) : null;
          const gPct = g != null ? toPct(g) : null;

          const parts: string[] = [];
          let cursor = 0;

          if (bPct != null) {
            parts.push(`rgb(23 23 23) ${cursor}%`, `rgb(23 23 23) ${bPct}%`); // neutral-900 相当
            cursor = bPct;
          }
          if (gPct != null && gPct > cursor) {
            parts.push(`rgb(163 163 163) ${cursor}%`, `rgb(163 163 163) ${gPct}%`); // neutral-400 相当（少し濃く）
            cursor = gPct;
          }
          if (cursor < 100) {
            parts.push(`rgb(187 247 208) ${cursor}%`, `rgb(187 247 208) 100%`); // green-200 相当（少し濃く）
          }

          const gradient = `linear-gradient(to right, ${parts.join(', ')})`;
          return { backgroundImage: gradient } as React.CSSProperties;
        })()}
      >
        {steps.map((s) => (
          <span key={s} className='w-4 h-4 md:w-9 md:h-9 inline-flex items-center justify-center'>
            <Dot step={s} total5={total5} />
          </span>
        ))}
      </div>
    </div>
  );
};

export const AsqSurveyResultPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  let payload: AsqResultPayload | undefined = undefined;

  // sessionStorage から読み込み
  const ssStr = typeof window !== 'undefined' ? sessionStorage.getItem('asq_result') : null;
  if (ssStr) {
    try {
      payload = JSON.parse(ssStr);
    } catch (_) {
      // ignore
    }
  }
  if (!payload) {
    payload = (location.state as AsqResultPayload) || {};
  }

  // 任意: 10分の期限（savedAt 同梱時）
  if (payload?.savedAt && Date.now() - payload.savedAt > 600_000) {
    sessionStorage.removeItem('asq_result');
    payload = { sections: [] };
  }

  const pageTitle = payload?.title ? `ASQ 結果 — ${payload.title}` : 'ASQ 結果';
  const sections = useMemo(() => payload?.sections ?? [], [payload?.sections]);

  // 刻みは5固定。最大点はデータに合わせて短縮し無駄な列を減らす（横スクロール抑制）
  const maxSteps = useMemo(() => {
    const vals = sections.flatMap((s) => [s.total_score ?? 0, s.cutoff_gray ?? 0, s.cutoff_black ?? 0]);
    const m = Math.max(0, ...vals);
    const m5 = Math.ceil(m / 5) * 5; // 5点刻みに丸め
    // 表示最大は 20〜60 の範囲にクランプ（必要に応じて調整可）
    return Math.min(60, Math.max(20, m5));
  }, [sections]);

  const steps = useSteps(maxSteps);

  const hasData = sections.length > 0 && sections.some((s) => s.total_score != null);

  return (
    <div className='min-h-screen flex flex-col bg-gray-50'>
      <AsqHeader />

      <main className='container mx-auto py-10 flex flex-col items-center gap-6 sm:gap-10 flex-1 w-full'>
        <div className='w-[80%] md:w-full'>
          <Title title={pageTitle} />
        </div>

        <div className='md:px-10 px-4 w-full mx-auto'>
          <div className='flex flex-col gap-6 sm:gap-10'>
            {!hasData && (
              <div className='text-center text-gray-600'>
                結果データが見つかりませんでした。前の画面から再度送信してください。
              </div>
            )}

            {hasData && (
              <>
                {/* 表レイアウト：ヘッダ + セクション行 */}
                <div className='w-full rounded-lg border border-gray-200'>
                  <div className='px-2 py-1 border-b border-gray-200 bg-gray-50'>
                    <StepsHeader steps={steps} />
                  </div>
                  <div className='divide-y divide-gray-200'>
                    {sections.map((sec) => (
                      <div key={sec.id} className='px-2 py-2 md:px-3 md:py-3'>
                        <SectionRow sec={sec} steps={steps} />
                      </div>
                    ))}
                  </div>
                </div>

                {/* 結果の解釈とフォローアップ */}
                <div className='text-sm text-gray-700 leading-relaxed mt-2'>
                  <div className='font-semibold mb-1'>結果の解釈とフォローアップ</div>
                  <ul className='list-disc pl-5 space-y-1'>
                    <li>お子さんの合計点が緑の領域：カットオフ値以上で発達は順調のようです。</li>
                    <li>お子さんの合計点が■（灰色）の領域：注意域で、カットオフ値に近く観察を要します。</li>
                    <li>
                      お子さんの合計点が■（黒色）の領域：要相談域で、カットオフ値以下のため専門家による詳細な評価を検討してください。
                    </li>
                  </ul>
                </div>
              </>
            )}

            <div className='flex justify-center'>
              <button
                className='btn btn-primary !h-8 !min-h-0 !px-2 !text-xs md:!h-9 md:!px-3 md:!text-sm'
                onClick={() => navigate('/asq')}
              >
                ASQ に戻る
              </button>
            </div>
          </div>
        </div>
      </main>

      <AsqFooter />
    </div>
  );
};

export default AsqSurveyResultPage;
